import React from 'react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  compareSectionSize,
  formatSectionSizePercentLabel,
} from '../utils/section-size-comparison';

interface SectionSizeCellProps {
  value: number;
  average: number;
  formattedValue: string;
  unitLabel: string;
  t: (key: string) => string;
}

const toneColors: Record<'average' | 'above' | 'below', string> = {
  average: '#6b7280',
  above: '#6bbf6b',
  below: '#f0ad73',
};

export const SectionSizeCell: React.FC<SectionSizeCellProps> = ({
  value,
  average,
  formattedValue,
  unitLabel,
  t,
}) => {
  const comparison = compareSectionSize(value, average);
  const percentLabel = formatSectionSizePercentLabel(comparison.percentDifference);
  const tooltipText = comparison.percentDifference === null
    ? t('section_size_no_average')
    : comparison.tone === 'average'
      ? t('section_size_at_average').replace('{average}', Math.round(average).toLocaleString()).replace('{unit}', unitLabel)
      : comparison.tone === 'above'
        ? t('section_size_above_average')
          .replace('{percent}', String(Math.abs(comparison.percentDifference ?? 0)))
          .replace('{average}', Math.round(average).toLocaleString())
          .replace('{unit}', unitLabel)
        : t('section_size_below_average')
          .replace('{percent}', String(Math.abs(comparison.percentDifference ?? 0)))
          .replace('{average}', Math.round(average).toLocaleString())
          .replace('{unit}', unitLabel);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '0.25rem' }}>
      <span>{formattedValue}</span>
      {percentLabel && (
        <TooltipProvider delayDuration={200}>
          <Tooltip>
            <TooltipTrigger asChild>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'flex-end',
                  gap: '0.125rem',
                  cursor: 'help',
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '4px',
                    backgroundColor: 'var(--muted)',
                    borderRadius: '9999px',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.round(comparison.barRatio * 50)}%`,
                      height: '100%',
                      backgroundColor: toneColors[comparison.tone],
                    }}
                  />
                </div>
                <span
                  style={{
                    fontSize: '0.6875rem',
                    fontWeight: 500,
                    color: toneColors[comparison.tone],
                  }}
                >
                  {percentLabel}
                </span>
              </div>
            </TooltipTrigger>
            <TooltipContent side="left">
              <p style={{ margin: 0 }}>{tooltipText}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
    </div>
  );
};
